import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@mui/material";

import Input from "../../Input";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useSelector } from "react-redux";
import messages from "../../../assets/locale/messages";
import { clx } from "../../../utils/Helpers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { UISelect, UITextField } from "../../ui";

import "./_CompanyEditForm.scss";

const GeneralInfoSection = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  cities,
  commercialRecord,
  disabled,
  classNames,
  hasTitleIcon,
  hideTitle,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const {
    company: t,
    companiesList: cl,
    inputsValidations: iv,
  } = messages[lang];

  const cityOptions = cities.map((c) => ({
    id: c.id,
    name: lang === "ar" ? c.name_ar || c.name_en : c.name_en || c.name_ar,
  }));

  const selectedCity =
    cityOptions.find((c) => String(c.id) === String(values.city_id)) ?? null;
  const nameEnHasError = !!(touched.name_en && errors.name_en);
  const nameArHasError = !!(touched.name_ar && errors.name_ar);
  const cityHasError = !!(touched.city_id && errors.city_id);
  const validationText = (key, fallbackKey = "generalFieldRequired") =>
    iv[key] || iv[fallbackKey] || key;

  return (
    <Box className={clx("section", classNames.base)}>
      {!hideTitle && (
        <div className="d-flex align-items-center gap-2">
          {hasTitleIcon && (
            <InfoOutlinedIcon fontSize="small" sx={{ color: "#155DFC" }} />
          )}
          <Typography className={clx("section-title", classNames.title)}>
            {cl.generalInformation}
          </Typography>
        </div>
      )}

      <Box
        className={classNames.content}
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        <Box
          className={clx(
            "section__input-group-name",
            classNames.inputGroupName,
          )}
        >
          <Input
            name="name_en"
            label={t.enName.label}
            value={values.name_en}
            required
            disabled={disabled}
            onChange={(val) => {
              setFieldTouched("name_en");
              setFieldValue("name_en", val);
            }}
            isInputHasErr={nameEnHasError}
            errMsg={
              nameEnHasError && iv[errors.name_en]
                ? errors.name_en
                : "enNameRequired"
            }
            inputWrapperClass="input-wrapper-en"
          />
          <Input
            name="name_ar"
            label={t.arName.label}
            value={values.name_ar}
            disabled={disabled}
            onChange={(val) => {
              setFieldTouched("name_ar");
              setFieldValue("name_ar", val);
            }}
            isInputHasErr={nameArHasError}
            errMsg={
              nameArHasError && iv[errors.name_ar]
                ? errors.name_ar
                : "arNameRequired"
            }
            inputWrapperClass="input-wrapper-ar"
          />
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <UISelect
              name="city_id"
              label={t.city.label}
              required
              options={cityOptions}
              value={selectedCity}
              disabled={disabled}
              onChange={(opt) => {
                setFieldTouched("city_id");
                setFieldValue("city_id", opt?.id ?? null);
              }}
              onBlur={() => setFieldTouched("city_id", true)}
              error={cityHasError}
              helperText={
                cityHasError ? validationText(errors.city_id, "cityRequired") : ""
              }
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <UITextField
              name="commercial_record"
              label={t.commercialRecord.label}
              value={commercialRecord}
              disabled
              helperText={
                <span className="helper-text">
                  <ErrorOutlineOutlinedIcon sx={{ fontSize: 12 }} />{" "}
                  {cl.cannotBeEdited}
                </span>
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

GeneralInfoSection.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  cities: PropTypes.array.isRequired,
  commercialRecord: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  classNames: PropTypes.object,
  hasTitleIcon: PropTypes.bool,
  hideTitle: PropTypes.bool,
};

GeneralInfoSection.defaultProps = {
  disabled: false,
  classNames: {
    base: "",
    content: "",
    inputGroupName: "",
  },
  hasTitleIcon: false,
  hideTitle: false,
};

export default GeneralInfoSection;
